import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { InfiniteData, useMutation, useQueryClient } from "@tanstack/react-query";
import { Media, MediaIndex } from "../types/media";
import { useMediaLikes, useUser, useMediaComments, useAlbumPermissions } from "../hooks";
import { MediaLikesModal } from "./shared";
import MediaCommentsBottomSheet from "./shared/MediaCommentsBottomSheet";
import AuthenticatedImage from "./AuthenticatedImage";
import AuthenticatedVideo from "./AuthenticatedVideo";
import { getAccessToken } from "../services/tokenHelper";
import DeleteMediaSelectionModal from "./DeleteMediaSelectionModal";
import OverlayRoot from "./shared/OverlayRoot";
import { authService } from "../services/authService";
import { mediaService } from "../services/mediaService";
import { albumKeys } from "../hooks/useAlbumsQuery";
import { mediaKeys } from "../hooks/useMediaQuery";
import { withoutMediaIndexItem } from "../utils/hydrateMediaFromIndex";
import { formatInstant, formatTakenAt } from "../utils/cameraLocalDateTime";
import { isVideoMedia } from "../utils/mediaKind";

const SWIPE_THRESHOLD_PX = 50;
const VIDEO_CONTROL_BAND_PX = 80;

const isEditableTarget = (target: EventTarget | null): boolean => {
  if (!target || !(target instanceof HTMLElement)) return false;
  const tag = target.tagName.toLowerCase();
  if (tag === "input" || tag === "textarea" || tag === "select") return true;
  return target.isContentEditable;
};

const isChromeSwipeTarget = (target: EventTarget | null): boolean => {
  if (!target || !(target instanceof Element)) return false;
  return Boolean(target.closest("button, a, input, textarea, select, [role='dialog']"));
};

const isVideoControlBand = (target: EventTarget | null, clientY: number): boolean => {
  if (!target || !(target instanceof Element)) return false;
  const video = target.closest("video");
  if (!video) return false;
  const rect = video.getBoundingClientRect();
  return clientY >= rect.bottom - VIDEO_CONTROL_BAND_PX;
};

interface ImageViewerProps {
  media: Media[];
  totalMediaCount?: number;
  currentMediaId: string;
  isOpen: boolean;
  onClose: () => void;
  ownerId?: string;
  albumId?: string;
}

const ImageViewer: React.FC<ImageViewerProps> = ({
  media,
  totalMediaCount,
  currentMediaId,
  isOpen,
  onClose,
  ownerId,
  albumId,
}) => {
  const queryClient = useQueryClient();
  const viewerRef = useRef<HTMLDivElement | null>(null);
  const hideControlsTimeoutRef = useRef<number | null>(null);
  const touchStartXRef = useRef<number | null>(null);
  const touchStartYRef = useRef<number | null>(null);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [mediaLoading, setMediaLoading] = useState(false);
  const [videoSource, setVideoSource] = useState<"original" | "playback">("original");
  const [showLikesModal, setShowLikesModal] = useState(false);
  const [showCommentsSheet, setShowCommentsSheet] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [isNativeFullscreen, setIsNativeFullscreen] = useState(false);
  const [isPseudoFullscreen, setIsPseudoFullscreen] = useState(false);
  const [controlsVisible, setControlsVisible] = useState(true);
  const [isNativeFullscreenSupported, setIsNativeFullscreenSupported] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);

  const isFullscreen = isNativeFullscreen || isPseudoFullscreen;
  const canFullscreen = true; // native when available, otherwise falls back to pseudo-fullscreen (needed for iOS Safari)

  const { user } = useUser();
  const currentMedia = media[currentIndex];
  const displayTotalMediaCount =
    totalMediaCount && totalMediaCount > 0 ? Math.max(totalMediaCount, media.length) : media.length;
  const isVideo = isVideoMedia(currentMedia);
  // Hide our chrome while native video controls are active to avoid overlap.
  const hideChromeForVideoPlayback = Boolean(isVideo && isVideoPlaying);
  const uploaderDeviceModel = currentMedia?.metadata?.uploadDeviceModel || currentMedia?.metadata?.deviceModel;
  const uploaderDeviceSerialNumber = currentMedia?.metadata?.deviceSerialNumber;
  const uploaderDeviceLabel =
    uploaderDeviceModel && uploaderDeviceSerialNumber
      ? `${uploaderDeviceModel} (${uploaderDeviceSerialNumber})`
      : uploaderDeviceModel || uploaderDeviceSerialNumber;
  const uploadedByLabel = currentMedia?.uploadedByEmail || uploaderDeviceLabel || "Unknown";

  const toggleCurrentVideoPlayback = useCallback(async () => {
    const container = viewerRef.current;
    if (!container) return;
    const video = container.querySelector("video") as HTMLVideoElement | null;
    if (!video) return;

    try {
      if (video.paused) {
        await video.play();
      } else {
        video.pause();
      }
    } catch (error) {
      console.warn("Failed to toggle video playback:", error);
    }
  }, []);

  const clearHideControlsTimeout = useCallback(() => {
    if (hideControlsTimeoutRef.current) {
      window.clearTimeout(hideControlsTimeoutRef.current);
      hideControlsTimeoutRef.current = null;
    }
  }, []);

  const scheduleHideControls = useCallback(() => {
    if (!isFullscreen) return;
    clearHideControlsTimeout();
    hideControlsTimeoutRef.current = window.setTimeout(() => {
      setControlsVisible(false);
    }, 1000);
  }, [clearHideControlsTimeout, isFullscreen]);

  const showControls = useCallback(() => {
    if (!isFullscreen) return;
    setControlsVisible(true);
    scheduleHideControls();
  }, [isFullscreen, scheduleHideControls]);

  const enterFullscreen = useCallback(async () => {
    try {
      const el = viewerRef.current;
      if (!el) return;
      if (!document.fullscreenEnabled) return;
      if (document.fullscreenElement) return;
      if (!el.requestFullscreen) return;

      await el.requestFullscreen();
      setControlsVisible(true);
      scheduleHideControls();
    } catch (error) {
      console.warn("Failed to enter fullscreen:", error);
    }
  }, [scheduleHideControls]);

  const exitFullscreen = useCallback(async () => {
    try {
      if (!document.fullscreenElement) return;
      await document.exitFullscreen();
    } catch (error) {
      console.warn("Failed to exit fullscreen:", error);
    }
  }, []);

  const enterPseudoFullscreen = useCallback(() => {
    setIsPseudoFullscreen(true);
    setControlsVisible(true);
    scheduleHideControls();
  }, [scheduleHideControls]);

  const exitPseudoFullscreen = useCallback(() => {
    setIsPseudoFullscreen(false);
    setControlsVisible(true);
    clearHideControlsTimeout();
  }, [clearHideControlsTimeout]);

  const toggleFullscreen = useCallback(() => {
    if (isNativeFullscreenSupported && viewerRef.current?.requestFullscreen) {
      if (document.fullscreenElement) {
        void exitFullscreen();
      } else {
        void enterFullscreen();
      }
      return;
    }

    // Fallback for browsers without the Fullscreen API (notably iOS Safari).
    if (isPseudoFullscreen) {
      exitPseudoFullscreen();
    } else {
      enterPseudoFullscreen();
    }
  }, [
    enterFullscreen,
    enterPseudoFullscreen,
    exitFullscreen,
    exitPseudoFullscreen,
    isNativeFullscreenSupported,
    isPseudoFullscreen,
  ]);

  const downloadOriginal = useCallback(async () => {
    if (!currentMedia?.originalUrl) return;

    try {
      const accessToken = await getAccessToken();
      if (!accessToken) {
        throw new Error("No access token available");
      }

      const response = await fetch(currentMedia.originalUrl, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to download media: ${response.status}`);
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = currentMedia.filename || "media";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error("Failed to download original media:", error);
    }
  }, [currentMedia?.originalUrl, currentMedia?.filename]);

  const formatMediaSize = (rawBytes: unknown): string => {
    const bytes =
      typeof rawBytes === "number" ? rawBytes : typeof rawBytes === "string" ? Number(rawBytes) : Number.NaN;

    if (!Number.isFinite(bytes) || bytes <= 0) return "Unknown";

    const KB = 1024;
    const MB = KB * 1024;
    const GB = MB * 1024;

    if (bytes < MB) {
      const kb = Math.max(1, Math.round(bytes / KB));
      return `${kb} KB`;
    }

    if (bytes < GB) {
      return `${(bytes / MB).toFixed(1)} MB`;
    }

    return `${(bytes / GB).toFixed(2)} GB`;
  };

  // Get permissions for this album
  const { canComment, canLike, canDeleteMedia } = useAlbumPermissions(ownerId, albumId);

  const deleteMediaMutation = useMutation({
    mutationFn: async (mediaId: string) => {
      if (!ownerId || !albumId) {
        throw new Error("Missing ownerId or albumId");
      }

      await authService.checkAuthentication();
      return mediaService.deleteMedia(ownerId, albumId, mediaId);
    },
    onSuccess: (_, mediaId) => {
      if (!ownerId || !albumId) return;

      // Update all cached lists for this album (regardless of sorting).
      queryClient.setQueriesData<InfiniteData<Media[]>>(
        { queryKey: mediaKeys.listBase(ownerId, albumId), exact: false },
        (old) => {
          if (!old) return old;
          return {
            ...old,
            pages: old.pages.map((page) => page.filter((m) => m.id !== mediaId)),
          };
        }
      );
      queryClient.setQueriesData<MediaIndex>({ queryKey: mediaKeys.indexBase(ownerId, albumId), exact: false }, (old) =>
        withoutMediaIndexItem(old, mediaId)
      );
      queryClient.removeQueries({ queryKey: mediaKeys.detail(ownerId, albumId, mediaId) });
      queryClient.invalidateQueries({ queryKey: mediaKeys.uploaders(ownerId, albumId) });

      // Invalidate album detail to refresh mediaCount + albums list to refresh thumbnails.
      queryClient.invalidateQueries({ queryKey: albumKeys.detail(ownerId, albumId) });
      queryClient.invalidateQueries({ queryKey: albumKeys.list(ownerId) });
    },
    onError: (error) => {
      console.error("Error deleting media:", error);
    },
  });

  const { likes, likesCount, isLikedByCurrentUser, isToggling, toggleLike } = useMediaLikes(
    ownerId,
    albumId,
    currentMedia?.id,
    user?.id
  );

  const { comments, hasComments } = useMediaComments(ownerId || "", albumId || "", currentMedia?.id || "");

  // If media changes (e.g. delete), keep index in range and close viewer if empty.
  useEffect(() => {
    if (!isOpen) return;

    if (media.length === 0) {
      onClose();
      return;
    }

    if (currentIndex >= media.length) {
      setCurrentIndex(Math.max(0, media.length - 1));
    }
  }, [currentIndex, isOpen, media.length, onClose]);

  // Find current media index (useLayoutEffect to avoid a 1-frame flash of the previous item on open)
  useLayoutEffect(() => {
    const index = media.findIndex((m) => m.id === currentMediaId);
    if (index !== -1) {
      setCurrentIndex(index);
    }
  }, [media, currentMediaId]);

  const goToPrevious = useCallback(() => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : media.length - 1));
  }, [media.length]);

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev < media.length - 1 ? prev + 1 : 0));
  }, [media.length]);

  // Fullscreen support + state sync (Esc or OS exit)
  useEffect(() => {
    setIsNativeFullscreenSupported(Boolean(document.fullscreenEnabled));

    const handleFullscreenChange = () => {
      const nowFullscreen = Boolean(document.fullscreenElement);
      setIsNativeFullscreen(nowFullscreen);
      if (!nowFullscreen) {
        setControlsVisible(true);
        clearHideControlsTimeout();
      } else {
        setControlsVisible(true);
        scheduleHideControls();
      }
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, [clearHideControlsTimeout, scheduleHideControls]);

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      // Don't hijack typing in inputs/textareas (e.g. comments).
      if (isEditableTarget(event.target)) return;

      switch (event.key) {
        case " ":
        case "Spacebar":
          event.preventDefault();
          // For videos: space toggles play/pause.
          if (isVideo) {
            void toggleCurrentVideoPlayback();
          }
          showControls();
          break;
        case "Enter":
          // For videos: enter toggles play/pause.
          if (isVideo) {
            event.preventDefault();
            void toggleCurrentVideoPlayback();
            showControls();
          }
          break;
        case "ArrowLeft":
        case "ArrowUp":
          event.preventDefault();
          goToPrevious();
          showControls();
          break;
        case "ArrowRight":
        case "ArrowDown":
          event.preventDefault();
          goToNext();
          showControls();
          break;
        case "f":
        case "F":
          if (canFullscreen) {
            event.preventDefault();
            toggleFullscreen();
          }
          break;
        case "Escape":
          event.preventDefault();
          if (document.fullscreenElement) {
            void exitFullscreen();
          } else if (isPseudoFullscreen) {
            exitPseudoFullscreen();
          } else {
            onClose();
          }
          break;
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [
    canFullscreen,
    exitFullscreen,
    exitPseudoFullscreen,
    goToPrevious,
    goToNext,
    isVideo,
    isOpen,
    isPseudoFullscreen,
    onClose,
    showControls,
    toggleCurrentVideoPlayback,
    toggleFullscreen,
  ]);

  const handleMediaLoad = useCallback(() => {
    setMediaLoading(false);
  }, []);

  const handleMediaError = useCallback(() => {
    // For mixed compatibility: try the original first (Safari/iOS often supports HEVC),
    // and only fall back to the server-provided playback URL if the original fails.
    if (
      isVideo &&
      videoSource === "original" &&
      currentMedia?.playbackUrl &&
      currentMedia?.originalUrl &&
      currentMedia.playbackUrl !== currentMedia.originalUrl
    ) {
      setVideoSource("playback");
      setMediaLoading(true);
      return;
    }

    setMediaLoading(false);
  }, [currentMedia?.originalUrl, currentMedia?.playbackUrl, isVideo, videoSource]);

  // Reset loading state when image changes (useLayoutEffect to avoid placeholder flicker on iOS Safari).
  // Keep the info panel open across prev/next — contents update with currentMedia.
  useLayoutEffect(() => {
    if (!isOpen) return;
    setMediaLoading(true);
    setVideoSource("original");
    setIsVideoPlaying(false);
  }, [currentIndex, isOpen, currentMedia?.url, currentMedia?.originalUrl, currentMedia?.playbackUrl]);

  const handleVideoPlay = useCallback(() => {
    setIsVideoPlaying(true);
    setShowInfo(false);
  }, []);

  const handleVideoPause = useCallback(() => {
    setIsVideoPlaying(false);
  }, []);

  const handleVideoEnded = useCallback(() => {
    setIsVideoPlaying(false);
  }, []);

  // Ensure we exit fullscreen when the viewer closes/unmounts.
  useEffect(() => {
    if (isOpen) return;
    clearHideControlsTimeout();
    setControlsVisible(true);
    if (document.fullscreenElement) {
      void exitFullscreen();
    }
    if (isPseudoFullscreen) {
      exitPseudoFullscreen();
    }
  }, [clearHideControlsTimeout, exitFullscreen, exitPseudoFullscreen, isOpen, isPseudoFullscreen]);

  // Cleanup any pending timers on unmount.
  useEffect(() => {
    return () => clearHideControlsTimeout();
  }, [clearHideControlsTimeout]);

  if (!isOpen || !currentMedia) return null;

  const fullscreenControlsClass = isFullscreen
    ? controlsVisible
      ? "opacity-100"
      : "opacity-0 pointer-events-none"
    : "opacity-100";

  const videoPlaybackChromeClass = hideChromeForVideoPlayback
    ? "opacity-0 pointer-events-none"
    : fullscreenControlsClass;

  const handleMouseMove = () => {
    showControls();
  };

  const swipeBlocked = showCommentsSheet || showLikesModal || isDeleteConfirmOpen || media.length <= 1;

  const handleTouchStart: React.TouchEventHandler = (event) => {
    if (swipeBlocked) return;
    if (event.touches.length !== 1) {
      touchStartXRef.current = null;
      touchStartYRef.current = null;
      return;
    }

    const t = event.touches[0];
    if (!t) return;
    if (isChromeSwipeTarget(event.target) || isVideoControlBand(event.target, t.clientY)) {
      touchStartXRef.current = null;
      touchStartYRef.current = null;
      return;
    }

    touchStartXRef.current = t.clientX;
    touchStartYRef.current = t.clientY;
  };

  const handleTouchEnd: React.TouchEventHandler = (event) => {
    const startX = touchStartXRef.current;
    const startY = touchStartYRef.current;
    touchStartXRef.current = null;
    touchStartYRef.current = null;

    if (swipeBlocked) {
      showControls();
      return;
    }

    const t = event.changedTouches[0];
    if (!t || startX === null || startY === null) {
      showControls();
      return;
    }

    const dx = t.clientX - startX;
    const dy = t.clientY - startY;
    const absDx = Math.abs(dx);
    const absDy = Math.abs(dy);

    // Horizontal swipe navigates; a small movement is treated as a tap (show controls).
    if (absDx > SWIPE_THRESHOLD_PX && absDx > absDy) {
      if (dx < 0) {
        goToNext();
      } else {
        goToPrevious();
      }
      showControls();
      return;
    }

    showControls();
  };

  return (
    <OverlayRoot
      data-section="image-viewer-root"
      ref={viewerRef}
      className="fixed inset-0 z-[60] flex touch-pan-y items-center justify-center bg-black/95 backdrop-blur-sm overscroll-none"
      onMouseMove={isFullscreen ? handleMouseMove : undefined}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={() => {
        touchStartXRef.current = null;
        touchStartYRef.current = null;
      }}
      onClick={isFullscreen ? showControls : undefined}
      onDragStart={(event) => event.preventDefault()}
    >
      {/* Close button */}
      <button
        onClick={() => {
          if (document.fullscreenElement) {
            void exitFullscreen();
            return;
          }
          if (isPseudoFullscreen) {
            exitPseudoFullscreen();
            return;
          }
          onClose();
        }}
        className={`absolute top-6 right-6 z-10 w-12 h-12 bg-white/10 hover:bg-white/20 backdrop-blur-xl border border-white/20 rounded-full flex items-center justify-center text-white hover:text-gray-200 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-white/50 ${fullscreenControlsClass}`}
        aria-label="Close image viewer"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>

      {/* Fullscreen toggle — hidden while native video playback controls are active */}
      {canFullscreen && (
        <button
          onClick={toggleFullscreen}
          className={`absolute top-6 left-6 z-10 w-12 h-12 bg-white/10 hover:bg-white/20 backdrop-blur-xl border border-white/20 rounded-full flex items-center justify-center text-white hover:text-gray-200 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-white/50 ${videoPlaybackChromeClass}`}
          aria-label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
          title={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
          aria-hidden={hideChromeForVideoPlayback}
          tabIndex={hideChromeForVideoPlayback ? -1 : undefined}
        >
          {isFullscreen ? (
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
              <path d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm11 8h-3v3h-2v-5h5v2zm-3-8V5h-2v5h5V8h-3z" />
            </svg>
          ) : (
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 3H5a2 2 0 00-2 2v3m18 0V5a2 2 0 00-2-2h-3m0 18h3a2 2 0 002-2v-3M3 16v3a2 2 0 002 2h3"
              />
            </svg>
          )}
        </button>
      )}

      {/* Persistent bottom action strip — hidden while native video controls are active */}
      <div
        className={`absolute bottom-0 left-0 right-0 z-10 flex items-center justify-center gap-2 px-4 transition-opacity duration-300 ${videoPlaybackChromeClass}`}
        style={{
          paddingBottom: `calc(${isVideo ? "5rem" : "1.5rem"} + env(safe-area-inset-bottom, 0px))`,
        }}
        aria-hidden={hideChromeForVideoPlayback}
      >
        <div className="flex items-center gap-1.5 bg-black/55 backdrop-blur-xl border border-white/15 rounded-2xl px-2 py-1.5">
          {/* Like */}
          {ownerId && albumId && (
            <button
              onClick={canLike ? toggleLike : undefined}
              disabled={!canLike || isToggling}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-xl transition-all duration-200 text-sm font-medium ${
                isLikedByCurrentUser ? "bg-red-500/25 text-red-400" : "text-white/70 hover:text-white hover:bg-white/10"
              } ${!canLike || isToggling ? "opacity-50 cursor-not-allowed" : ""}`}
              title={canLike ? (isLikedByCurrentUser ? "Unlike" : "Like") : "Sign in to like"}
            >
              {isToggling ? (
                <div className="w-4 h-4 border border-current border-t-transparent rounded-full animate-spin" />
              ) : (
                <svg
                  className="w-4 h-4"
                  fill={isLikedByCurrentUser ? "currentColor" : "none"}
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                  />
                </svg>
              )}
              {likesCount > 0 && <span>{likesCount}</span>}
            </button>
          )}

          {/* Comments */}
          {ownerId && albumId && (
            <button
              onClick={() => setShowCommentsSheet(true)}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-white/70 hover:text-white hover:bg-white/10 transition-all duration-200 text-sm font-medium"
              title="View comments"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                />
              </svg>
              {hasComments && <span>{comments.length}</span>}
            </button>
          )}

          {/* Separator */}
          <div className="w-px h-5 bg-white/15 mx-0.5" />

          {/* Info toggle */}
          <button
            onClick={() => setShowInfo(!showInfo)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-xl transition-all duration-200 text-sm font-medium ${
              showInfo ? "bg-white/15 text-white" : "text-white/70 hover:text-white hover:bg-white/10"
            }`}
            aria-label={showInfo ? "Hide details" : "Show details"}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </button>

          {/* Download */}
          {currentMedia?.originalUrl && (
            <button
              onClick={downloadOriginal}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-white/70 hover:text-white hover:bg-white/10 transition-all duration-200"
              title="Download original"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                />
              </svg>
            </button>
          )}

          {/* Delete */}
          {currentMedia && canDeleteMedia(currentMedia) && (
            <button
              onClick={() => setIsDeleteConfirmOpen(true)}
              disabled={deleteMediaMutation.isPending}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-red-400/70 hover:text-red-400 hover:bg-red-500/15 transition-all duration-200 disabled:opacity-50"
              title="Delete"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Media counter — same format as the mobile viewer (`2 / 33 · Video`) */}
      <div
        className={`absolute top-6 left-1/2 transform -translate-x-1/2 z-10 bg-black/50 backdrop-blur-xl border border-white/20 rounded-full px-4 py-2 text-white text-sm font-medium transition-opacity duration-300 ${videoPlaybackChromeClass}`}
      >
        {displayTotalMediaCount > 0 ? `${currentIndex + 1} / ${displayTotalMediaCount}` : ""}
        {isVideo ? " · Video" : ""}
      </div>

      {/* Previous button */}
      {media.length > 1 && (
        <button
          onClick={goToPrevious}
          className={`absolute left-6 top-1/2 transform -translate-y-1/2 z-10 w-12 h-12 bg-white/10 hover:bg-white/20 backdrop-blur-xl border border-white/20 rounded-full flex items-center justify-center text-white hover:text-gray-200 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-white/50 ${videoPlaybackChromeClass}`}
          aria-label="Previous image"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
      )}

      {/* Next button */}
      {media.length > 1 && (
        <button
          onClick={goToNext}
          className={`absolute right-6 top-1/2 transform -translate-y-1/2 z-10 w-12 h-12 bg-white/10 hover:bg-white/20 backdrop-blur-xl border border-white/20 rounded-full flex items-center justify-center text-white hover:text-gray-200 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-white/50 ${videoPlaybackChromeClass}`}
          aria-label="Next image"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      )}

      {/* Main media — fill the overlay like the mobile viewer (contain-fit, chrome overlays). */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Loading spinner — videos render their own overlay inside AuthenticatedVideo */}
        {mediaLoading && !isVideo && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-12 h-12 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          </div>
        )}

        {/* Main media */}
        {isVideo ? (
          currentMedia?.originalUrl || currentMedia?.playbackUrl ? (
            <AuthenticatedVideo
              presignEndpoint={currentMedia.playbackUrl as string}
              variant={videoSource}
              poster={currentMedia.url}
              // Keep the element visible while loading so the `poster` thumbnail can be shown behind the spinner.
              className="h-full w-full object-contain"
              playsInline
              onLoadedData={handleMediaLoad}
              onError={handleMediaError}
              onPlay={handleVideoPlay}
              onPause={handleVideoPause}
              onEnded={handleVideoEnded}
            />
          ) : (
            <div className="text-white">Video unavailable</div>
          )
        ) : (
          currentMedia.url && (
            <AuthenticatedImage
              src={currentMedia.url}
              alt={currentMedia.filename}
              className={`block h-full w-full select-none object-contain transition-opacity duration-300 ${
                mediaLoading ? "opacity-0" : "opacity-100"
              }`}
              onLoad={handleMediaLoad}
              onError={handleMediaError}
              loading="eager"
            />
          )
        )}

        {/* Image info overlay — metadata only; actions live in the bottom strip */}
        {showInfo && (
          <div className="absolute bottom-20 left-4 right-4 sm:left-6 sm:right-6 sm:bottom-24 bg-black/60 backdrop-blur-xl border border-white/15 rounded-2xl p-4 text-white transition-all duration-300 animate-in fade-in slide-in-from-bottom-4">
            <h3 className="font-semibold text-base truncate mb-1">{currentMedia.filename}</h3>
            <div className="flex items-center justify-between text-xs text-gray-300 mb-3">
              <span>{currentMedia.contentType}</span>
              {currentMedia.metadata?.width && currentMedia.metadata?.height && (
                <span>
                  {currentMedia.metadata.width} × {currentMedia.metadata.height}
                </span>
              )}
            </div>
            <div className="space-y-1.5 text-sm text-gray-200">
              <div className="flex items-center justify-between gap-3">
                <span className="text-gray-400 whitespace-nowrap">Uploaded by</span>
                <span className="truncate">{uploadedByLabel}</span>
              </div>
              <div className="flex items-center justify-between gap-3">
                <span className="text-gray-400 whitespace-nowrap">Taken at</span>
                <span className="truncate">
                  {currentMedia.metadata?.takenAt ? formatTakenAt(currentMedia.metadata?.takenAt) : "Unknown"}
                </span>
              </div>
              <div className="flex items-center justify-between gap-3">
                <span className="text-gray-400 whitespace-nowrap">Uploaded at</span>
                <span className="truncate">{formatInstant(currentMedia.createdAt)}</span>
              </div>
              <div className="flex items-center justify-between gap-3">
                <span className="text-gray-400 whitespace-nowrap">Size</span>
                <span className="truncate">
                  {formatMediaSize(
                    currentMedia.originalSize ??
                      (currentMedia as unknown as { original_size?: unknown }).original_size ??
                      currentMedia.metadata?.fileSize
                  )}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Click outside to close */}
      <div className="absolute inset-0 -z-10" onClick={onClose} aria-label="Click outside to close" />

      {/* Media Likes Modal */}
      <MediaLikesModal
        isOpen={showLikesModal}
        onClose={() => setShowLikesModal(false)}
        likes={likes}
        mediaName={currentMedia?.filename}
        isLoading={false}
      />

      {/* Media Comments Bottom Sheet */}
      {ownerId && albumId && currentMedia && (
        <MediaCommentsBottomSheet
          isOpen={showCommentsSheet}
          onClose={() => setShowCommentsSheet(false)}
          ownerId={ownerId}
          albumId={albumId}
          mediaId={currentMedia.id}
          mediaName={currentMedia.filename}
          canComment={canComment}
        />
      )}

      <DeleteMediaSelectionModal
        isOpen={isDeleteConfirmOpen}
        selectedCount={1}
        isDeleting={deleteMediaMutation.isPending}
        onClose={() => {
          if (deleteMediaMutation.isPending) return;
          setIsDeleteConfirmOpen(false);
        }}
        onConfirm={async () => {
          if (!currentMedia) return;
          try {
            await deleteMediaMutation.mutateAsync(currentMedia.id);
          } finally {
            setIsDeleteConfirmOpen(false);
          }
        }}
      />
    </OverlayRoot>
  );
};

export default ImageViewer;
